import { FC, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import moment from "moment";
import FormCheckbox from "../../components/FormCheckbox";
import { FormDatePicker, FormTextInput } from "../../components/FormFields";
import FormLabel from "../../components/FormLabel";
import HorizontalPadding from "../../components/HorizontalPadding";
import LoadingIcon from "../../components/LodingIcon";
import PaginationFooter from "../../components/PaginationFooter";
import { PrimaryButton } from "../../components/Buttons";
import { AuditActionOption, AuditLogRequest, AuditLogRow } from "../../interfaces";
import { getAuditLog, searchAuditActions } from "../../services";
import { useAppSelector } from "../../store/hooks";

type FiltersState = {
  user: string;
};

const ROWS_PER_PAGE = 20;

const AuditLogPage: FC = () => {
  const businessUnit = useAppSelector((state) => state.user.businessUnit);

  const today = useMemo(() => new Date(), []);
  const [minDate, setMinDate] = useState<Date | null>(
    moment(today).subtract(7, "days").toDate()
  );
  const [maxDate, setMaxDate] = useState<Date | null>(today);
  const [filters, setFilters] = useState<FiltersState>({
    user: "",
  });
  const [actions, setActions] = useState<AuditActionOption[]>([]);
  const [selectedActionIDs, setSelectedActionIDs] = useState<number[]>([]);
  const [actionSearchInput, setActionSearchInput] = useState("");
  const [debouncedActionSearch, setDebouncedActionSearch] = useState("");
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsDropdownRef = useRef<HTMLDivElement | null>(null);

  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itemOffset, setItemOffset] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (!businessUnit?.code) return;
    // ensure pagination resets when BU changes
    setItemOffset(0);
  }, [businessUnit?.code]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedActionSearch(actionSearchInput.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [actionSearchInput]);

  useEffect(() => {
    let cancelled = false;
    const loadActions = async () => {
      try {
        const res = await searchAuditActions(undefined, debouncedActionSearch || undefined);
        if (!cancelled) {
          setActions(res ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setActions([]);
        }
      }
    };
    loadActions();
    return () => {
      cancelled = true;
    };
  }, [debouncedActionSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionsDropdownRef.current &&
        !actionsDropdownRef.current.contains(event.target as Node)
      ) {
        setActionsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const filteredActions = useMemo(() => {
    const term = actionSearchInput.trim().toLowerCase();
    if (!term) return actions;
    return actions.filter(
      (a) =>
        a.shortName.toLowerCase().includes(term) ||
        (a.description?.toLowerCase().includes(term) ?? false)
    );
  }, [actionSearchInput, actions]);

  const paginatedRows = useMemo(() => {
    return rows.slice(itemOffset, itemOffset + ROWS_PER_PAGE);
  }, [rows, itemOffset]);

  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * ROWS_PER_PAGE) % rows.length;
    setItemOffset(newOffset);
  };

  useEffect(() => {
    setTotalPages(Math.ceil(rows.length / ROWS_PER_PAGE));
  }, [rows.length, itemOffset]);

  const buildRequest = (): AuditLogRequest | null => {
    if (!minDate || !maxDate) {
      setError("Debe indicar fecha inicio y fin.");
      return null;
    }
    if (!businessUnit?.code) {
      setError("No hay unidad de negocio activa en la sesión.");
      return null;
    }

    const auditActions = selectedActionIDs.length ? selectedActionIDs : undefined;
    const user = filters.user.trim();

    return {
      // normalizar a inicio y fin de día en hora local antes de serializar
      dateStart: moment(minDate).startOf("day").toDate().toISOString(),
      dateEnd: moment(maxDate).endOf("day").toDate().toISOString(),
      entityID: null,
      auditActions,
      user: user || undefined,
      buCode: businessUnit.code,
    };
  };

  const handleSearch = async (event?: FormEvent) => {
    if (event) event.preventDefault();
    setError(null);
    const payload = buildRequest();
    if (!payload) return;

    setLoading(true);
    try {
      const result = await getAuditLog(payload);
      if (result === null) {
        setRows([]);
        setError("No se pudieron obtener los registros de auditoría.");
        return;
      }
      setRows(result);
    } catch (err) {
      setRows([]);
      setError("Error inesperado al consultar auditoría.");
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (value: string | null) => {
    if (!value) return "-";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "-" : parsed.toLocaleString();
  };

  return (
    <main className="lg:pl-72">
      <div className="py-6 sm:px-6 lg:px-8 bg-white relative flex items-center justify-between h-32 mb-12">
        <header className="ml-4 text-2xl font-bold text-gray-700 ">Reporte de Auditoría</header>
      </div>

      <HorizontalPadding>
        <form
          className="bg-white border rounded-lg px-6 py-5 mb-6 flex flex-col gap-4"
          onSubmit={handleSearch}
        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div className="w-full">
                <FormDatePicker
                  id="audit-date-start"
                  name="audit-date-start"
                  label="Desde"
                  useRange={false}
                  maxDate={maxDate}
                  value={{ startDate: minDate, endDate: minDate }}
                  onChange={(e) =>
                    setMinDate(!!e?.startDate ? moment(e.startDate).toDate() : null)
                  }
                  toggleClassName={(old) => classNames(old, "text-indigo-600")}
                  configs={{ shortcuts: {} }}
                />
              </div>
              <div className="w-full">
                <FormDatePicker
                  id="audit-date-end"
                  name="audit-date-end"
                  label="Hasta"
                  useRange={false}
                  minDate={minDate}
                  value={{ startDate: maxDate, endDate: maxDate }}
                  onChange={(e) =>
                    setMaxDate(!!e?.startDate ? moment(e.startDate).toDate() : null)
                  }
                  toggleClassName={(old) => classNames(old, "text-indigo-600")}
                  configs={{ shortcuts: {} }}
                />
              </div>
              <div
                ref={actionsDropdownRef}
                className="flex flex-col relative"
              >
                <FormLabel fieldName="Acciones" />
                <div
                  role="button"
                  aria-haspopup="listbox"
                  aria-expanded={actionsOpen}
                  onClick={() => setActionsOpen((o) => !o)}
                  className={classNames(
                    // mirrored from inputClassName in FormFields for visual consistency
                    "focus block w-full rounded-xl border-0 text-gray-400 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 h-10",
                    "truncate pl-4 pr-8 flex items-center justify-between cursor-pointer"
                  )}
                >
                  <span className="text-gray-900">
                    {selectedActionIDs.length > 0
                      ? `${selectedActionIDs.length} seleccionada(s)`
                      : "Selecciona acciones"}
                  </span>
                  <span className="text-gray-400">▾</span>
                </div>

                {actionsOpen && (
                  <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 p-3 flex flex-col gap-2">
                    <FormTextInput
                      id="audit-actions-search"
                      label="Buscar acción"
                      value={actionSearchInput}
                      onChange={(e) => setActionSearchInput(e.target.value)}
                      placeholder="Escribe parte del nombre"
                    />
                    <div className="max-h-48 overflow-auto flex flex-col gap-1">
                      {filteredActions.map((a) => {
                        const checked = selectedActionIDs.includes(a.actionID);
                        return (
                          <FormCheckbox
                            key={a.actionID}
                            id={`audit-action-${a.actionID}`}
                            label={a.shortName}
                            description={a.description ?? undefined}
                            checked={checked}
                            onChange={(next) => {
                              setSelectedActionIDs((prev) => {
                                if (next) {
                                  return prev.includes(a.actionID)
                                    ? prev
                                    : [...prev, a.actionID];
                                }
                                return prev.filter((id) => id !== a.actionID);
                              });
                            }}
                          />
                        );
                      })}
                      {filteredActions.length === 0 && (
                        <span className="text-xs text-gray-500 px-2 py-1">Sin coincidencias.</span>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 text-xs">
                      <button
                        type="button"
                        className="text-indigo-600"
                        onClick={() => setSelectedActionIDs([])}
                      >
                        Limpiar
                      </button>
                      <button
                        type="button"
                        className="text-indigo-600"
                        onClick={() => setActionsOpen(false)}
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="w-full">
                <FormTextInput
                  id="audit-user"
                  label="Usuario"
                  value={filters.user}
                  onChange={(e) => setFilters((p) => ({ ...p, user: e.target.value }))}
                  placeholder="user.login"
                />
              </div>
            <div className="flex items-end justify-end">
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? "Buscando..." : "Buscar"}
              </PrimaryButton>
            </div>
          </div>

          {error && <span className="text-sm text-red-600">{error}</span>}
        </form>

        <div className="bg-white border rounded-lg px-6 py-5 mb-24">
          <div
            className={classNames(
              "w-auto",
              (rows.length === 0 || loading) && "hidden"
            )}
          >
            <div className="overflow-x-auto">
              <table className="table-auto w-full text-left text-sm">
                <thead>
                  <tr className="text-xs text-gray-600 uppercase">
                    <th className="px-4 py-2">Fecha</th>
                    <th className="px-4 py-2">Acción</th>
                    <th className="px-4 py-2">Descripción</th>
                    <th className="px-4 py-2">Usuario</th>
                    {/* <th className="px-4 py-2">IP</th> */}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((row, index) => (
                    <tr
                      key={`audit-row-${index}`}
                      className={classNames(index % 2 === 0 ? "bg-white" : "bg-gray-50")}
                    >
                      <td className="px-4 py-2 align-top">{formatDateTime(row.auditDate)}</td>
                      <td className="px-4 py-2 align-top">{row.shortName ?? "-"}</td>
                      <td className="px-4 py-2 align-top">{row.description ?? "-"}</td>
                      <td className="px-4 py-2 align-top">{row.actionUser ?? "-"}</td>
                      {/* <td className="px-4 py-2 align-top">{row.ipAddress ?? "-"}</td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationFooter
              totalPages={totalPages}
              itemsOffSet={itemOffset}
              rowCounts={ROWS_PER_PAGE}
              totalItems={rows.length}
              handlePageClick={handlePageClick}
            />
          </div>

          <div
            className={classNames(
              "flex flex-1 items-center justify-center min-h-[10rem]",
              (rows.length > 0 || loading) && "hidden"
            )}
          >
            <p className="text-gray-400">Sin resultados.</p>
          </div>

          <div
            className={classNames(
              "flex items-center justify-center w-full p-8",
              !loading && "hidden"
            )}
          >
            <LoadingIcon size="2rem" />
          </div>
        </div>
      </HorizontalPadding>
    </main>
  );
};

export default AuditLogPage;
